import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig
} from "axios";
import config from "config/config";
import { instance } from "./apiBase";

const API_BASE = config.apiHost;

// Runtime
let accessToken: string | null = null;

const REFRESH_TOKEN_KEY = "portal_refresh_token";

// to prevent 401 loop refresh.
let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;

export interface JwtTokens {
  access_token: string;
  refresh_token: string;
}

export const jwt = {
  getAccessToken(): string | null {
    return accessToken;
  },

  // Read refresh token (localStorage)
  getRefreshToken(): string | null {
    if (typeof window === "undefined") return null;
    try {
      return localStorage.getItem(REFRESH_TOKEN_KEY);
    } catch {
      return null;
    }
  },

  // Set both tokens (persist refresh only)
  setTokens(tokens: JwtTokens) {
    accessToken = tokens.access_token;
    if (typeof window !== "undefined")
      try {
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      } catch {
        // ignore
      }
  },

  // Clear tokens from memory and storage
  clearTokens() {
    accessToken = null;
    if (typeof window !== "undefined")
      try {
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      } catch {
        // ignore
      }
  },

  // Ensure we have a valid access token in memory (may perform refresh)
  async ensureAccessToken(): Promise<void> {
    if (accessToken) return;
    const rt = jwt.getRefreshToken();
    if (!rt) return; // No session
    await jwt.refresh(rt);
  },

  // Perform refresh with rotation
  async refresh(refreshToken?: string): Promise<void> {
    const rt = refreshToken ?? jwt.getRefreshToken();
    if (!rt) throw new Error("No refresh token");

    if (isRefreshing && refreshPromise) return refreshPromise;

    isRefreshing = true;
    refreshPromise = fetch(`${API_BASE}/portal/v1/auth/jwt/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt })
    })
      .then(async (res) => {
        if (!res.ok) {
          // On refresh failure, nuke tokens
          jwt.clearTokens();
          throw new Error("Refresh failed");
        }
        const data: JwtTokens = await res.json();
        jwt.setTokens(data);
      })
      .finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });

    return refreshPromise;
  },

  async impersonate(token?: string): Promise<boolean> {
    if (!token) return false;

    const response = await instance.post<{
      access_token: string;
      refresh_token: string;
    }>("/portal/v1/auth/jwt/exchange", {
      token_key: token
    });

    const tokens = response.data;

    jwt.clearTokens();
    jwt.setTokens(tokens);

    return true;
  },

  // Attach axios interceptors for Authorization + auto-refresh
  attachInterceptors(client: AxiosInstance) {
    client.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
      const at = jwt.getAccessToken();
      if (at) cfg.headers.Authorization = `Bearer ${at}`;
      return cfg;
    });

    client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const { response, config: cfg } = error;
        if (!response || !cfg) throw error;

        // Avoid infinite loop
        // @ts-expect-error custom flag
        if (cfg.__isRetry) throw error;

        if (response.status === 401)
          try {
            await jwt.refresh();
            const at = jwt.getAccessToken();
            if (!at) throw error;
            // @ts-expect-error custom flag
            cfg.__isRetry = true;
            cfg.headers.Authorization = `Bearer ${at}`;
            return client(cfg);
          } catch {
            jwt.clearTokens();
            throw error;
          }

        throw error;
      }
    );
  }
};
